import { safeLocalStorage, generateId } from './app-utils';

// --- Constants ---
export const STORAGE_KEYS = {
  TASKS: 'productivity_tasks',
  SESSIONS: 'productivity_sessions',
  TIMER_STATE: 'productivity_timer_state',
  SETTINGS: 'productivity_settings'
};

export const POMODORO_SETTINGS = {
  FOCUS_TIME: 25 * 60, // 25 mins in seconds
  SHORT_BREAK: 5 * 60,
  LONG_BREAK: 15 * 60,
  SESSIONS_BEFORE_LONG_BREAK: 4
};

// --- Data Access ---
export const getTasks = (userId) => {
  const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.TASKS) || '[]');
  return userId ? all.filter(t => t.userId === userId) : all;
};

export const saveTasks = (tasks) => {
  safeLocalStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
};

export const getSessions = (userId) => {
  const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.SESSIONS) || '[]');
  return userId ? all.filter(s => s.userId === userId) : all;
};

export const saveSession = (session) => {
  const all = getSessions(null); // Get all to append
  all.push(session);
  safeLocalStorage.setItem(STORAGE_KEYS.SESSIONS, JSON.stringify(all));
};

// --- Timer Persistence ---
export const saveTimerState = (state) => {
  safeLocalStorage.setItem(STORAGE_KEYS.TIMER_STATE, JSON.stringify(state));
};

export const getTimerState = () => {
  return JSON.parse(localStorage.getItem(STORAGE_KEYS.TIMER_STATE) || 'null');
};

// --- Analytics Helpers ---
export const getDateRangeData = (userId, days = 7) => {
  const sessions = getSessions(userId);
  const tasks = getTasks(userId);
  const now = new Date();
  const data = [];

  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    
    // Filter for this day
    const daySessions = sessions.filter(s => s.date === dateStr);
    const dayTasks = tasks.filter(t => t.date === dateStr || (t.completedAt && t.completedAt.startsWith(dateStr)));

    const focusMinutes = Math.round(daySessions.reduce((acc, s) => acc + (s.duration / 60), 0));
    const completedTasks = dayTasks.filter(t => t.status === 'completed' && t.completedAt?.startsWith(dateStr)).length;

    data.push({
      date: dateStr,
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
      focusMinutes,
      completedTasks,
      sessionsCount: daySessions.length
    });
  }
  return data;
};

export const getWeeklyStats = (userId) => {
  const rangeData = getDateRangeData(userId, 7);
  const totalFocus = rangeData.reduce((acc, d) => acc + d.focusMinutes, 0);
  const totalCompleted = rangeData.reduce((acc, d) => acc + d.completedTasks, 0);
  
  // Find most productive day
  const mostProductive = rangeData.reduce((prev, current) => 
    (prev.focusMinutes > current.focusMinutes) ? prev : current
  );

  return {
    totalFocus,
    totalCompleted,
    mostProductiveDay: mostProductive.dayName,
    dailyData: rangeData
  };
};

export const downloadCSV = (data, filename) => {
  const headers = Object.keys(data[0]);
  const csvContent = "data:text/csv;charset=utf-8," 
    + [headers.join(','), ...data.map(row => headers.map(fieldName => JSON.stringify(row[fieldName])).join(','))].join('\n');
  
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};